import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { ComplianceCourseProgressModel } from "../models/dashboard/compliance-course-progress.model";
import { EmployeesIddProgressModel } from "../models/dashboard/employees-idd-progress.model";
import { LearningPathProgressModel } from "../models/dashboard/learning-path-progress.model";
import { PersonIddProgressModel } from "../models/dashboard/person-idd-progress.model";
import { QualificationStatusPersonPerMonthModel } from "../models/dashboard/qualification-status-person-per-month.model";
import { TrainingGroupIddProgressModel } from "../models/dashboard/training-groups-idd-progress.model";
import { TopRecordsModel } from "../models/top-records/top-records.model";
import { DashboardComparisonStatisticsModel } from "../models/statistics/dashboard-comparison-statistics.model";
import { CompletedEventCategoryModel } from "../models/completed-event/completed-event-category.model";
import { DashboardCompletedEventCategoryModel } from "../models/completed-event/dashboard-completed-event-category.model";
import { ChannelProgressModel } from "../models/dashboard/channel-progress.model";
import { PendingRequestSummaryModel } from "../models/dashboard/pending-request-summary.model";
import { LearningRecommendationModel } from "../models/learning-recommendations/learning-recommendation.model";

@Injectable({ providedIn: 'root' })
export class DashboardWidgetsDataService {
  private dashboardStatisticsSubject = new BehaviorSubject<DashboardComparisonStatisticsModel | null>(null);
  private top10RecordsSubject = new BehaviorSubject<TopRecordsModel | null>(null);
  private employeesProgressSubject = new BehaviorSubject<EmployeesIddProgressModel | null>(null);
  private personProgressSubject = new BehaviorSubject<PersonIddProgressModel | null>(null);
  private complianceCoursesProgressSubject = new BehaviorSubject<TrainingGroupIddProgressModel[] | null>(null);
  private trainingGroupsProgressSubject = new BehaviorSubject<ComplianceCourseProgressModel[] | null>(null);
  private learningPathsProgressSubject = new BehaviorSubject<LearningPathProgressModel[] | null>(null);
  private learningPathsProgressCurrentSubject = new BehaviorSubject<LearningPathProgressModel[] | null>(null);
  private competencyMatrixDataSubject = new BehaviorSubject<DashboardCompletedEventCategoryModel[] | null>(null);
  private personCompetencyMatrixDataSubject = new BehaviorSubject<CompletedEventCategoryModel[] | null>(null);
  private trendToTargetIddDataSubject = new BehaviorSubject<QualificationStatusPersonPerMonthModel[] | null>(null);
  private pendingRequestDataSubject = new BehaviorSubject<PendingRequestSummaryModel[] | null>(null);
  private channelProgressDataSubject = new BehaviorSubject<ChannelProgressModel[] | null>(null);
  private learningRecommendationsSubject = new BehaviorSubject<LearningRecommendationModel[] | null>(null);

  // Setters:

  setDashboardStatistics(data: DashboardComparisonStatisticsModel) {
    this.dashboardStatisticsSubject.next(data);
  }

  setTop10Records(data: TopRecordsModel) {
    this.top10RecordsSubject.next(data);
  }

  setEmployeesProgress(data: EmployeesIddProgressModel) {
    this.employeesProgressSubject.next(data);
  }

  setPersonProgress(data: PersonIddProgressModel) {
    this.personProgressSubject.next(data);
  }

  setComplianceCoursesProgress(data: TrainingGroupIddProgressModel[]) {
    this.complianceCoursesProgressSubject.next(data);
  }

  setTrainingGroupsProgress(data: ComplianceCourseProgressModel[]) {
    this.trainingGroupsProgressSubject.next(data);
  }

  setLearningPathsProgress(data: LearningPathProgressModel[]) {
    this.learningPathsProgressSubject.next(data);
  }

  setLearningPathsProgressCurrent(data: LearningPathProgressModel[]) {
    this.learningPathsProgressCurrentSubject.next(data);
  }

  setCompetencyMatrixData(data: DashboardCompletedEventCategoryModel[]) {
    this.competencyMatrixDataSubject.next(data);
  }

  setPersonCompetencyMatrixData(data: CompletedEventCategoryModel[]) {
    this.personCompetencyMatrixDataSubject.next(data);
  }

  setTrendToTargetIddData(data: QualificationStatusPersonPerMonthModel[]) {
    this.trendToTargetIddDataSubject.next(data);
  }

  setPendingRequestData(data: PendingRequestSummaryModel[]) {
    this.pendingRequestDataSubject.next(data);
  }

  setChannelProgressData(data: ChannelProgressModel[]) {
    this.channelProgressDataSubject.next(data);
  }

  setLearningRecommendations(data: LearningRecommendationModel[]) {
    this.learningRecommendationsSubject.next(data);
  }

  // Getters:

  getDashboardStatistics(): Observable<DashboardComparisonStatisticsModel | null> {
    return this.dashboardStatisticsSubject.asObservable();
  }

  getTop10Records(): Observable<TopRecordsModel | null> {
    return this.top10RecordsSubject.asObservable();
  }

  getEmployeesProgress(): Observable<EmployeesIddProgressModel | null> {
    return this.employeesProgressSubject.asObservable();
  }

  getPersonProgress(): Observable<PersonIddProgressModel | null> {
    return this.personProgressSubject.asObservable();
  }

  getComplianceCoursesProgress(): Observable<TrainingGroupIddProgressModel[] | null> {
    return this.complianceCoursesProgressSubject.asObservable();
  }

  getTrainingGroupsProgress(): Observable<ComplianceCourseProgressModel[] | null> {
    return this.trainingGroupsProgressSubject.asObservable();
  }

  getLearningPathsProgress(): Observable<LearningPathProgressModel[] | null> {
    return this.learningPathsProgressSubject.asObservable();
  }

  getLearningPathsProgressCurrent(): Observable<LearningPathProgressModel[] | null> {
    return this.learningPathsProgressCurrentSubject.asObservable();
  }

  getCompetencyMatrixData(): Observable<DashboardCompletedEventCategoryModel[] | null> {
    return this.competencyMatrixDataSubject.asObservable();
  }

  getPersonCompetencyMatrixData(): Observable<CompletedEventCategoryModel[] | null> {
    return this.personCompetencyMatrixDataSubject.asObservable();
  }

  getTrendToTargetIddData(): Observable<QualificationStatusPersonPerMonthModel[] | null> {
    return this.trendToTargetIddDataSubject.asObservable();
  }

  getPendingRequestData(): Observable<PendingRequestSummaryModel[] | null> {
    return this.pendingRequestDataSubject.asObservable();
  }

  getChannelProgressData(): Observable<ChannelProgressModel[] | null> {
    return this.channelProgressDataSubject.asObservable();
  }

  getLearningRecommendations(): Observable<LearningRecommendationModel[] | null> {
    return this.learningRecommendationsSubject.asObservable();
  }
}
